import React from 'react'
import PropTypes from 'prop-types'
import { parseISO, format, addDays } from 'date-fns'
import deprecatedPropType from './helpers/deprecatedPropType/deprecatedPropType'
import CalendarRoot from './CalendarRoot'
import keyNavHandler from './helpers/keyNavHandler/keyNavHandler'

const now = Date.now()
// If no max or min date is supplied then use these defaults.
// However if a minDate is supplied w/o a maxDate then calculate the max.
const defaultToday = format(now, 'yyyyMMdd')
const defaultMaxDate = format(addDays(now, 330), 'yyyyMMdd')
export const DayMapContext = React.createContext()

/**
 * Generic Calendar component
 */
class Calendar extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      startDate: props.startDate || 0,
      endDate: props.endDate || 0,
      scrollDate: props.scrollDate || 0,
      maxSelectableDate: props.maxDate,
      didUseKeyNavigation: false,
      hoveredDate: Calendar.getInitialHoveredDate(props),
    }

    this.calendarRootRef = React.createRef()

    this.handleKeyNav = this.handleKeyNav.bind(this)
    this.setCalendarRef = this.setCalendarRef.bind(this)
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDismiss)
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDismiss)
  }

  // Update startDate and endDate from outside changes
  UNSAFE_componentWillReceiveProps(nextProps) {
    const nextState = {}
    if (this.props.startDate !== nextProps.startDate) {
      nextState.startDate = nextProps.startDate
    }
    if (this.props.endDate !== nextProps.endDate) {
      nextState.endDate = nextProps.endDate
    }
    if (Object.keys(nextState).length > 0) {
      this.setState(nextState)
    }
  }

  static getInitialHoveredDate({ startDate, endDate, minDate, selectTwoDates }) {
    // TODO: If the parameters passed are in the correct format then this is just validating format. Perhaps it is best to validate format before getting here.
    const fallbackDate = minDate ? format(parseISO(minDate, 'yyyyMMdd'), 'yyyyMMdd') : format(now, 'yyyyMMdd')
    if (selectTwoDates) {
      return endDate ? format(parseISO(endDate, 'yyyyMMdd'), 'yyyyMMdd') : fallbackDate
    } else {
      return startDate ? format(parseISO(startDate, 'yyyyMMdd'), 'yyyyMMdd') : fallbackDate
    }
  }

  handleKeyDismiss = (event) => {
    if (event.keyCode === 27) {
      event.preventDefault()
      event.stopPropagation()
      this.props.onDismiss(event)
    }
  }

  handleKeyNav(event) {
    const { hoveredDate, maxSelectableDate } = this.state
    const { minDate } = this.props

    // Only preventDefault if it's an action that we've accounted for (e.g. don't prevent tabbing away)
    const { action, date } = keyNavHandler(event, hoveredDate, maxSelectableDate, minDate)

    if (action) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (date && action === 'NAVIGATE') {
      this.onDayHovered(event, date, true)
    } else if (date && action === 'SELECT') {
      this.onDayClicked(event, date)
    }
  }

  onDayHovered(event, hoveredDate, keyNav) {
    const { startDate, endDate, didUseKeyNavigation } = this.state
    const { selectTwoDates } = this.props

    if (endDate && !keyNav) return

    const updatedState = { hoveredDate }

    if (keyNav && ((!selectTwoDates && startDate) || (selectTwoDates && startDate && endDate))) {
      updatedState.startDate = 0
      updatedState.endDate = 0
    }

    if (!didUseKeyNavigation && keyNav) {
      updatedState.didUseKeyNavigation = true
    }

    this.setState(updatedState)
  }

  onDayClicked(event, date) {
    if (this.props.selectTwoDates) {
      if (
        (this.state.startDate && this.state.endDate) ||
        !this.state.startDate ||
        date < this.state.startDate
      ) {
        // Max day selection length feature
        let maxSelectableDate = this.props.maxDate
        if (this.props.maxDaysToSelect && this.props.maxDaysToSelect > 1) {
          // Hardcoded -2 is to account for start date and end date.
          // Presumably there is some sort of upstream check that prevents this from being < 3
          maxSelectableDate = format(
            addDays(parseISO(date, 'yyyyMMdd'), this.props.maxDaysToSelect - 2),
            'yyyyMMdd'
          )
        }
        this.setState({
          startDate: date,
          endDate: 0,
          hoveredDate: date,
          maxSelectableDate,
        })
        this.props.onChange(event, { startDate: date, endDate: null })
      } else if (
        this.state.startDate && this.props.allowSameDay
          ? date >= this.state.startDate
          : date > this.state.startDate
      ) {
        this.setState({ endDate: date, maxSelectableDate: this.props.maxDate })
        this.props.onChange(event, {
          startDate: this.state.startDate,
          endDate: date,
        })
      }
    } else {
      this.props.onChange(event, { startDate: date })
      this.setState({ startDate: date, endDate: 0 })
    }
  }

  setCalendarRef = (ref) => {
    this.calendarRef = ref

    const callback = this.props.setCalendarRef || this.props.setCalenderRef

    callback && callback(ref)
  }

  render() {
    return (
      <DayMapContext.Provider
        value={{
          dayMap: this.props.dayMap,
          renderDayFn: this.props.renderDayFn,
        }}
      >
        <CalendarRoot
          {...this.props}
          {...this.state}
          onDayClicked={this.onDayClicked.bind(this)}
          onDayHovered={this.onDayHovered.bind(this)}
          keyNavHandler={this.handleKeyNav}
          showHoveredDates={this.props.selectTwoDates && this.props.showHoveredDates}
          monthWidth={this.props.monthWidthPx}
          setCalendarRef={this.setCalendarRef}
          animateScroll={this.props.animateScroll}
          locale={this.props.locale}
        />
      </DayMapContext.Provider>
    )
  }
}

export const CalendarPropTypes = {
  animateScroll: PropTypes.bool,
  /** Allow same day selection */
  allowSameDay: PropTypes.bool,
  /** Render box shadow around Calendar */
  boxShadow: PropTypes.bool,
  /** Callback to execute when selection changes */
  onChange: PropTypes.func,
  /** Selected start date */
  startDate: PropTypes.string,
  /** Selected end date */
  endDate: PropTypes.string,
  /** Date to scroll to if specified other than startDate */
  scrollDate: PropTypes.string,
  /** Earliest selectable date */
  minDate: PropTypes.string,
  /** Latest selectable date */
  maxDate: PropTypes.string,
  /** Number of months to display at once */
  monthToShow: PropTypes.number,
  /** Show the year in header */
  displayYear: PropTypes.bool,
  /** Begin weeks on Sunday instead of Monday */
  startsWithSunday: deprecatedPropType('locale'),
  locale: PropTypes.string,
  /** Style days when hovered */
  showHoveredDates: PropTypes.bool,
  /** Allow selection of two dates instead of one */
  selectTwoDates: PropTypes.bool,
  /** Maximum range between start and end date selection */
  maxDaysToSelect: PropTypes.number,
  setCalenderRef: deprecatedPropType('setCalendarRef'),
  /** Callback to set ref to Calendar */
  setCalendarRef: PropTypes.func,
  /** Map of arbitrary values to corresponding dates */
  dayMap: PropTypes.object,
  /** Function to render a custom Day */
  renderDayFn: PropTypes.func,
  /** Callback executed when the months displayed change */
  onDisplayMonthsChange: PropTypes.func,
  /** Object used to override/set style for Day cell wrapper such as margin */
  dayWrapperStyleOverride: PropTypes.object,
  /** Callback to set ref of selected Day */
  setSelectedDayRef: PropTypes.func,
  /** Callback executed when user closes calendar via footer or ESC key */
  onDismiss: PropTypes.func,
  /** Use the default footer that provides accessibility features */
  useDefaultFooter: PropTypes.bool,
  /** Pixel width of each month */
  monthWidthPx: PropTypes.number,
}

export const CalendarDefaultProps = {
  minDate: defaultToday,
  maxDate: defaultMaxDate,
  allowSameDay: false,
  boxShadow: true,
  monthToShow: 2,
  displayYear: true,
  showHoveredDates: typeof window !== 'undefined' && window.innerWidth > 640,
  selectTwoDates: true,
  onChange: () => {},
  dayMap: {},
  renderDayFn: () => {},
  monthWidthPx: 350,
}

Calendar.propTypes = CalendarPropTypes

Calendar.defaultProps = CalendarDefaultProps

export default Calendar
